import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackClick } from '@/services/tracking';
import { cleanAddress, displayName } from '@/seo/text';
import { entityPath } from '@/seo/urls';
import { StoreListItem } from '@/components/cards';
import {
  CITY_STORES_VISIBLE,
  STORE_GROUPS_OPEN_MAX,
  STORE_ROWS_RICH_MAX,
  cityKey,
  storesByCity,
} from '@/seo/brandStores';
import { sitePath } from '@/seo/cityHubs';
import type { Media, Store, StoreMinimal } from '@/types';

type StoreRow = Store | StoreMinimal;

interface CityCount {
  city: {
    name: string;
    slug: string;
  };
  stores: number;
  brandCityPath?: string | null;
}

/**
 * Chain stores share one logo, so rows surface name / address / city instead
 * of a card grid. Every row is a `<li>` holding the row's link to its store.
 * `city` is the label of the city the rows are grouped under (the heading's),
 * which every row's chip then shows: "dha lahore" and "DHA Lahore" rows both
 * read "DHA Lahore" under "DHA Lahore (2)".
 */
function StoreRows({
  stores,
  city,
  className,
}: {
  stores: readonly StoreRow[];
  city?: string | null;
  className?: string;
}) {
  return (
    <ul className={`flex flex-col gap-2.5 ${className ?? ''}`}>
      {stores.map((store) => (
        <li key={store.id}>
          <StoreListItem store={store} city={city} />
        </li>
      ))}
    </ul>
  );
}

/**
 * The light row of a big brand's closed city group: the store's link (name,
 * then its address as plain text) and nothing else. About 150 bytes of HTML
 * against ~2.7 KB for the full row, yet still one crawlable `<a href>` per
 * store (audit F1).
 */
function LightStoreRows({ stores }: { stores: readonly StoreRow[] }) {
  return (
    // Styled from the list, not per row: no class or `data-discover` attribute
    // on each of hundreds of rows.
    <ul className="flex flex-col gap-1 px-4 pb-3 text-sm text-ink-secondary [&_a]:font-semibold [&_a]:text-ink [&_a:hover]:text-primary">
      {stores.map((store) => {
        const name = displayName(store.name);
        const address = cleanAddress((store as Store).address);
        // Many names already end with the address ("Bata — Liberty Market, Lahore").
        const showAddress = address && !name.includes(address);
        return (
          <li key={store.id}>
            <Link to={entityPath(store, 'store')} discover="none" onClick={() => trackClick('store', store.id)}>
              {name}
            </Link>
            {showAddress ? ` · ${address}` : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One city group. `light` groups (big brands, closed) render LightStoreRows
 * until the visitor opens the `<details>`; the full rows replace them then.
 * The first client render equals the server's (`expanded` starts false), so
 * hydration matches.
 */
function CityGroup({
  city,
  stores,
  open,
  light,
  headingContent,
  brandCityPath,
}: {
  city: string | null;
  stores: readonly StoreRow[];
  open: boolean;
  light: boolean;
  headingContent: string;
  brandCityPath: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const rich = !light || expanded;

  return (
    <details
      open={open}
      onToggle={light ? (e) => e.currentTarget.open && setExpanded(true) : undefined}
      className="group/city rounded-2xl border border-card-border bg-card"
    >
      {/* The summary is the group's toggle: nothing interactive inside it
          (a link there would be nested interactive content). */}
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl px-4 py-3 transition-colors hover:bg-surface [&::-webkit-details-marker]:hidden">
        <h3 className="min-w-0 break-words text-base font-bold text-ink">{headingContent}</h3>
        <ChevronDown
          size={18}
          aria-hidden="true"
          className="shrink-0 text-ink-tertiary transition-transform group-open/city:rotate-180"
        />
      </summary>
      {brandCityPath && city ? (
        <p className="px-4 pb-1 text-sm">
          <Link to={sitePath(brandCityPath) || ''} className="font-semibold text-primary-strong hover:underline">
            Stores and offers in {city}
          </Link>
        </p>
      ) : null}
      {rich ? (
        <StoreRows stores={stores} city={city} className="px-2 pb-2 sm:px-3 sm:pb-3" />
      ) : (
        <LightStoreRows stores={stores} />
      )}
    </details>
  );
}

/**
 * A brand's stores, EVERY one of them, grouped by city (web plan W0.10): one
 * `<details>` per city whose `<summary>` is the `<h3>{City} ({n})</h3>`,
 * biggest city first; stores without a city come last under "Other
 * locations". A closed group only hides its rows — they stay in the DOM —
 * so every store link is in the rendered HTML for crawlers (the old "Show
 * all N stores" button left all but 12 of them out) and in-page find can
 * open a group. Short lists (≤ STORE_GROUPS_OPEN_MAX stores) start open.
 * Past STORE_ROWS_RICH_MAX stores a closed group's rows are light (name +
 * address links) until it is opened.
 */
export function StoreList({
  stores,
  cityCounts,
}: {
  stores: readonly StoreRow[];
  cityCounts?: readonly CityCount[];
}) {
  const groups = storesByCity(stores);
  const total = groups.reduce((n, group) => n + group.stores.length, 0);
  const open = total <= STORE_GROUPS_OPEN_MAX;
  // Big brands: closed groups start with light rows (see STORE_ROWS_RICH_MAX).
  const light = !open && total > STORE_ROWS_RICH_MAX;

  return (
    <div className="mt-4 space-y-3">
      {groups.map((group) => {
        // The brand × city hub of a group's city: `group.key` is the city's
        // label in lower case ("rahim yar khan"), so it is matched on the
        // count's city name the same way, never on its slug ("rahim-yar-khan",
        // or one with a suffix after a collision).
        let brandCityPath: string | null = null;
        if (group.key && cityCounts) {
          const cityCount = cityCounts.find((count) => cityKey(count.city.name) === group.key);
          brandCityPath = cityCount?.brandCityPath || null;
        }

        const headingContent = `${group.city ?? 'Other locations'} (${group.stores.length})`;

        return (
          <CityGroup
            key={group.key || '(no city)'}
            city={group.city}
            stores={group.stores}
            open={open}
            light={light}
            headingContent={headingContent}
            brandCityPath={brandCityPath}
          />
        );
      })}
    </div>
  );
}

/**
 * Stores in one city (a store page's "Other {Brand} stores in {City}"): the
 * first CITY_STORES_VISIBLE rows, then the rest behind "Show N more", still
 * in the DOM. Rows without a logo of their own show `fallbackLogo` (the
 * brand's): the store query fetches the brand's other stores without media.
 * `city` is the section heading's city label, which every row's chip shows.
 */
export function CityStoreList({
  stores,
  fallbackLogo,
  city,
}: {
  stores: readonly StoreRow[];
  fallbackLogo?: Media | null;
  city?: string | null;
}) {
  const rows = fallbackLogo
    ? stores.map((store) => (store.logo ? store : { ...store, logo: fallbackLogo }))
    : stores;
  const shown = rows.slice(0, CITY_STORES_VISIBLE);
  const rest = rows.slice(CITY_STORES_VISIBLE);

  return (
    <div className="mt-4">
      <StoreRows stores={shown} city={city} />
      {rest.length > 0 && (
        <details className="group/more mt-2.5">
          <summary className="flex w-full cursor-pointer list-none items-center justify-center gap-1 rounded-2xl border border-card-border bg-card py-2.5 text-sm font-semibold text-primary-strong transition-colors hover:bg-surface [&::-webkit-details-marker]:hidden">
            <span className="group-open/more:hidden">
              Show {rest.length} more {rest.length === 1 ? 'store' : 'stores'}
            </span>
            <span className="hidden group-open/more:inline">Show less</span>
            <ChevronDown
              size={16}
              aria-hidden="true"
              className="transition-transform group-open/more:rotate-180"
            />
          </summary>
          <StoreRows stores={rest} city={city} className="mt-2.5" />
        </details>
      )}
    </div>
  );
}

export default StoreList;
